import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import JsonLd from '../components/JsonLd';
import { getSiteOrigin } from '../lib/siteMeta';
import { formations as mockFormations } from '../mock/mockData';
import { fetchFormationById } from '../lib/api';
import { cleanFormationTitle, isProgrammeTag } from '../lib/formationDisplay';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Award, Clock } from 'lucide-react';

const typeColors = {
  'E-Learning': 'bg-sky-50 text-sky-700 border-sky-200',
  Présentiel: 'bg-amber-50 text-amber-700 border-amber-200',
  Mixte: 'bg-purple-50 text-purple-700 border-purple-200',
  Hybride: 'bg-violet-50 text-violet-800 border-violet-200',
  'Classe Virtuelle': 'bg-cyan-50 text-cyan-800 border-cyan-200',
  'Évaluation Pratique': 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const fmtEuro = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(n));
};

const isDev = process.env.NODE_ENV === 'development';

const FormationDetailPage = () => {
  const { id } = useParams();
  const [formation, setFormation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchFormationById(id)
      .then((f) => {
        if (!alive) return;
        if (f) setFormation(f);
        else if (isDev) {
          const numId = Number(id);
          setFormation(mockFormations.find((x) => x.id === numId) || null);
        } else setFormation(null);
      })
      .catch(() => {
        if (!alive) return;
        if (isDev) {
          const numId = Number(id);
          setFormation(mockFormations.find((x) => x.id === numId) || null);
        } else setFormation(null);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/40">
        <Seo
          title="Formation"
          description="Chargement de la fiche formation Medelia Santé (Qualiopi, professionnels de santé, France)."
          canonicalPath={`/formations/${id}`}
        />
        <Navbar />
        <main className="safe-pt-nav pb-16 px-5 max-w-3xl mx-auto text-center">
          <p className="text-slate-600">Chargement…</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!formation) {
    return (
      <div className="min-h-screen bg-slate-50/40">
        <Seo
          title="Formation introuvable"
          description="Cette formation n’existe pas ou n’est plus au catalogue Medelia."
          canonicalPath={`/formations/${id}`}
          noindex
        />
        <Navbar />
        <main className="safe-pt-nav pb-16 px-5 max-w-3xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-slate-900">Formation introuvable</h1>
          <p className="text-slate-600 mt-2">Cette formation n’existe pas ou n’est plus au catalogue.</p>
          <Button asChild className="mt-6">
            <Link to="/formations">Retour au catalogue</Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const title = cleanFormationTitle(formation.title);
  const origin = getSiteOrigin();
  const orgId = `${origin}/#organization`;
  const formationPath = `/formations/${formation.id}`;
  const formationUrl = `${origin}${formationPath}`;
  const detailDescription = `Formation ${formation.type} — ${formation.category}. Durée ${formation.hours} h. Tarif ${fmtEuro(formation.price)} €. Inscription et financement (DPC, FIFPL…) accompagnés par Medelia en France.`;

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${origin}/` },
      { '@type': 'ListItem', position: 2, name: 'Formations', item: `${origin}/formations` },
      { '@type': 'ListItem', position: 3, name: title, item: formationUrl },
    ],
  };

  const courseLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: title,
    description: detailDescription,
    url: formationUrl,
    provider: { '@id': orgId },
    audience: {
      '@type': 'EducationalAudience',
      educationalRole: formation.category || 'Professionnel de santé',
    },
    timeRequired: `PT${Number(formation.hours) || 0}H`,
    offers: {
      '@type': 'Offer',
      price: formation.price,
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      url: formationUrl,
    },
  };

  return (
    <div className="min-h-screen bg-slate-50/40">
      <Seo title={title} description={detailDescription} canonicalPath={formationPath} />
      <JsonLd data={[breadcrumbLd, courseLd]} />
      <Navbar />
      <main className="safe-pt-nav">
        <section className="border-b border-slate-100 bg-white">
          <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10">
            <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2 min-h-11 text-slate-600 touch-manipulation">
              <Link to="/formations">
                <ArrowLeft className="w-4 h-4 mr-1" /> Catalogue
              </Link>
            </Button>
            <nav aria-label="Fil d'Ariane" className="mb-6 text-sm text-slate-600">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li>
                  <Link to="/" className="text-teal-700 hover:underline">
                    Accueil
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li>
                  <Link to="/formations" className="text-teal-700 hover:underline">
                    Formations
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li className="text-slate-500 line-clamp-1 max-w-[min(100%,14rem)] sm:max-w-md">{title}</li>
              </ol>
            </nav>
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge variant="outline" className={`text-xs font-bold border ${typeColors[formation.type] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                {formation.type}
              </Badge>
              <Badge variant="outline" className="text-xs font-medium bg-slate-50 text-slate-700 border-slate-200">
                {formation.category}
              </Badge>
              {formation.popular && <Badge className="bg-teal-600 text-white hover:bg-teal-600 text-xs">Tendance</Badge>}
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight break-words">{title}</h1>
            <div className="mt-6 flex flex-wrap gap-3 text-sm text-slate-600">
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-teal-600" /> {formation.hours} heures
              </span>
              <span className="flex items-center gap-2">
                <Award className="w-4 h-4 text-teal-600" /> DPC
              </span>
              {formation.tag && (
                <span
                  className={
                    isProgrammeTag(formation.tag)
                      ? 'rounded-lg border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-medium text-teal-900'
                      : 'rounded-lg border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700'
                  }
                >
                  {formation.tag}
                </span>
              )}
            </div>
            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-end justify-between border-t border-slate-100 pt-8">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Tarif</p>
                <p className="text-3xl font-bold text-slate-900 tabular-nums">{fmtEuro(formation.price)}&nbsp;€</p>
                {formation.indemnite > 0 && (
                  <p className="text-sm text-emerald-600 font-semibold mt-1">+ {fmtEuro(formation.indemnite)} € indemnité possible</p>
                )}
              </div>
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto min-h-12 bg-teal-600 hover:bg-teal-700 text-white shadow-md shadow-teal-600/20 touch-manipulation"
              >
                <Link to={`/inscription?formation=${formation.id}`}>S'inscrire à cette formation</Link>
              </Button>
            </div>
          </div>
        </section>
        <section className="max-w-4xl mx-auto px-5 lg:px-8 py-12">
          <h2 className="text-lg font-bold text-slate-900 mb-3">À propos de cette formation</h2>
          <p className="text-slate-600 leading-relaxed">
            Formation certifiée pour professionnels de santé. Modalité <strong>{formation.type}</strong>, discipline{' '}
            <strong>{formation.category}</strong>. Cliquez sur « S'inscrire » pour laisser vos coordonnées : un conseiller vous rappelle pour
            finaliser votre dossier et votre financement (DPC, FIFPL, etc.).
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FormationDetailPage;
